import { NgInject } from '@df/ng/ng-inject';
import { App } from '@df/core/app';
import compact from 'lodash-es/compact';
import drop from 'lodash-es/drop';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import first from 'lodash-es/first';
import forOwn from 'lodash-es/forOwn';
import keyBy from 'lodash-es/keyBy';
import keys from 'lodash-es/keys';
import reduce from 'lodash-es/reduce';
import without from 'lodash-es/without';
import { BundleSelection } from '@df/catalog/product/bundle-selection';
import { IProductDataBundleOption, IProductDataBundleOptionChild, IProductDataV3, Product } from '@df/catalog/product/product';

/**
 * Its an object with qties for each selected option for given qty
 */
export interface IBundleSelectionOptions {
    [key: string]: BundleOptionSelection;
}

export interface IBundleSelectionOptionsData {
    [key: string]: IBundleOptionSelectionSelection;
}

export interface IBundleSelectionProducts {
    [productId: string]: IBundleSelectionProduct;
}

export interface IBundleSelectionProduct {
    product: Product<IProductDataV3>;
    qty: number;
    order: number;
}

/**
 * Its an object with qties for each selected option for given qty
 */
export interface IBundleOptionSelectionSelection {
    [key: string]: number;
}

export class BundleOptionSelection {
    protected _selection: IBundleOptionSelectionSelection = {};

    /**
     * Flag indicating if this is one or multi selection option
     * @type {boolean}
     */
    protected _isOneSelection: boolean;

    /**
     * Flag indicating if this option cannot be changed
     * is required with one options and has no editable qty
     * @type {boolean}
     */
    protected _editable: boolean = true;

    /**
     * Internal index for faster child retrieval
     */
    protected _childrenIndex?: {
        [selectionId: string]: IProductDataBundleOptionChild;
    };

    @NgInject(App)
    private app!: App;

    /**
     * Class constructor
     * @param {IProductDataBundleOption} protected _bundleOption
     * @param {IBundleOptionSelectionSelection} data Selection to apply initially
     */
    constructor(
        protected _bundleSelection: BundleSelection,
        protected _bundleOption: IProductDataBundleOption,
        data?: IBundleOptionSelectionSelection
    ) {
        this._isOneSelection = this._bundleOption.type === 'radio' || this._bundleOption.type === 'select';
        this.setData(data);

        // set non editable if needed
        if (this._isOneSelection && this._bundleOption.children?.length < 2) {
            const child = this._bundleOption.children[0];
            if (!child || child.fixedQty) {
                this._editable = false;
            }
        }
    }

    get id(): number {
        return this._bundleOption.id;
    }

    /////////////////////////////////////////////
    ///
    /// Selection manipulation
    ///
    /////////////////////////////////////////////

    /**
     * Toggle option selection
     * @param {number | IProductDataBundleOptionChild} selection
     */
    toggle(selection: number | IProductDataBundleOptionChild) {
        if (this._editable) {
            this.isPicked(selection) ? this.removeItem(selection) : this.setItem(selection);
        }
    }

    /**
     * Sets item qty in bundle selection
     *
     * If option is type checkbox then will add this one without affecting any other selections
     * If option is type dropdown will replace any existing selections
     *
     * @param {number | IProductDataBundleOptionChild} selection If number then treated as selectionId
     * @param {number}    qty  Qty to add for given selection, default is qty defined as default one
     */
    setItem(selection: number | IProductDataBundleOptionChild, qty?: number, noCheck: boolean = false) {
        if (this._editable) {
            const selectionId = this._toSelectionId(selection);

            if (typeof qty !== 'number') {
                qty = this.getChild(selection).qty;
            }
            this._selection[selectionId] = qty;
            if (!noCheck) {
                this.onSelectionChange(selectionId);
            }
        }
    }

    /**
     * Adds item to bundle selection
     *
     * If option is type checkbox then will add this one without affecting any other selections
     * If option is type dropdown will replace any existing selections
     *
     * @param {number | IProductDataBundleOptionChild} selection If number then treated as selectionId
     * @param {number}    qty  Qty to add for given selection, default 1
     */
    addItem(selection: number | IProductDataBundleOptionChild, qty: number = 1) {
        if (this._editable) {
            const selectionId = this._toSelectionId(selection);
            this._selection[selectionId] = (this._selection[selectionId] || 0) + qty;
            this.onSelectionChange(selectionId);
        }
    }

    /**
     * Removes items from selection
     * @param {number | IProductDataBundleOptionChild} selection [description]
     * @param {number}    qty  Qty to remove, if not provided will remove it completely
     */
    removeItem(selection: number | IProductDataBundleOptionChild, qty?: number) {
        if (this._editable) {
            const selectionId = this._toSelectionId(selection);
            if (this._selection[selectionId]) {
                if (qty === undefined) {
                    qty = this._selection[selectionId];
                }

                // check if we can drop that option
                if (qty >= this._selection[selectionId]) {
                    if (this._bundleOption.required && keys(this._selection).length <= 1) {
                        return;
                    }
                }

                this._selection[selectionId] -= qty;
                this.onSelectionChange();
            }
        }
    }

    /**
     * Removes all items from selection
     */
    removeAll() {
        if (this._editable) {
            forOwn(this._selection, (value, selectionId) => {
                delete this._selection[selectionId];
            });
            this.onSelectionChange();
        }
    }

    /**
     * Returns true if selection is not empty
     * @return {boolean}
     */
    get hasSelection() {
        return this._selection && !!Object.keys(this._selection).length;
    }

    /**
     * Completely sets selection by request
     * If no data given defaul selection will be applied
     * @param {IBundleOptionSelectionSelection} data
     */
    setData(data?: IBundleOptionSelectionSelection) {
        // clear selection
        forOwn(this._selection, (value, selectionId) => {
            delete this._selection[selectionId];
        });

        // add selection
        if (data) {
            forOwn(data, (value, selectionId) => {
                this.setItem(parseInt(selectionId, 10), data[selectionId], true);
            });
        }
        this.addDefaultSelections(!data);

        // recheck
        this.onSelectionChange();
    }

    /////////////////////////////////////////////
    ///
    /// Getting information
    ///
    /////////////////////////////////////////////

    /**
     * Gets selected qty
     * @param {number | IProductDataBundleOptionChild} selection
     * @returns {boolean} qty selected for that option
     */
    getQty(selection: number | IProductDataBundleOptionChild): number {
        return this._selection[this._toSelectionId(selection)] || 0;
    }

    /**
     * Gets selected qty
     * @param {number | IProductDataBundleOptionChild} selection
     * @returns {boolean} true if options is selectied
     */
    isPicked(selection: number | IProductDataBundleOptionChild): boolean {
        return !!this.getQty(selection);
    }

    /**
     * Returns true if this is single choice option
     * @return {boolean}
     */
    get isOneSelection(): boolean {
        return this._isOneSelection;
    }

    /**
     * Returns true if product options can be changed (apart from qty)
     * @return {boolean}
     */
    get editable(): boolean {
        return this._editable;
    }

    /**
     * If any selection is required
     * @return {boolean}
     */
    get required(): boolean {
        return this._bundleOption.required;
    }

    /**
     * Checks if qty for given selection is editable
     * @param {selection: number | IProductDataBundleOptionChild} selection
     */
    isQtyEditable(selection: number | IProductDataBundleOptionChild) {
        const child = this.getChild(selection);
        return child && !child.fixedQty;
    }

    /**
     * Gettter for bundle option
     */
    get option() {
        return this._bundleOption;
    }

    /**
     * Gettter for bundle option
     */
    get children() {
        return this._bundleOption.children;
    }

    /**
     * Called when selection changes
     * @param {number | string} selectionToStay [description]
     */
    protected onSelectionChange(selectionToStay?: number | string): void {
        this.cleanupBadSelections();
        this.removeExtraSelections(selectionToStay);
        this.addDefaultSelections();
        this._bundleSelection.next();
    }

    /**
     * Returns selection object
     * DO NOT MANIPULATE THAT OBJECT OUTSIDE as it may cause unexpected problems
     * @return {IBundleOptionSelectionSelection}
     */
    get selection(): IBundleOptionSelectionSelection {
        return this._selection;
    }

    /**
     * Returns child matching given selection or selection id
     * @param  {number | IBundleSelectionOption} selection
     * @return {IProductDataBundleOptionChild}   Maching child
     */
    getChild(selection: number | IProductDataBundleOptionChild): IProductDataBundleOptionChild {
        if (!this._childrenIndex) {
            this._childrenIndex = keyBy(this._bundleOption.children, 'selection_id');
        }
        return this._childrenIndex[this._toSelectionId(selection)];
    }

    /**
     * Returns selected products with qties
     */
    getSelectedProducts(): IBundleSelectionProducts {
        const map: IBundleSelectionProducts = {};
        let order = 0;
        forOwn(this._selection, (value, selectionId) => {
            const child = this.getChild(parseInt(selectionId, 10));
            if (child) {
                if (!map[child.id]) {
                    map[child.id] = {
                        product: this.app.resources.product.getExisting(child.id) as Product<IProductDataV3>,
                        qty: 0,
                        order: order++
                    };
                }
                map[child.id].qty += this._selection[selectionId];
            }
        });
        return map;
    }

    getPrice(): number {
        return this.calcPrice(false);
    }

    getOrgPrice(): number {
        return this.calcPrice(true);
    }

    calcPrice(orgPrice: boolean = false): number {
        const c = reduce(
            this.getSelectedProducts(),
            (memo: number, selection: IBundleSelectionProduct) => {
                const product = selection.product;
                if (product) {
                    return memo + selection.qty * this.getComponentPrice(product, orgPrice);
                }
                return memo;
            },
            0
        );

        return c;
    }

    protected getComponentPrice(product: Product<IProductDataV3>, orgPrice: boolean): number {
        return orgPrice ? product.orgPrice || product.price : product.price;
    }

    /////////////////////////////////////////////
    ///
    /// Internal things
    ///
    /////////////////////////////////////////////

    /**
     * when options is required and not picked then apply default selection
     * if resetToDefaultMode then we reset selection to default
     * if !resetToDefaultMode then we preselect options when required
     */
    protected addDefaultSelections(resetToDefaultMode = false) {
        if (resetToDefaultMode) {
            this.removeExtraSelections();
        }
        if ((this._bundleOption.required && keys(this._selection).length < 1) || resetToDefaultMode) {
            // local gunction to put selection
            const put = (child?: IProductDataBundleOptionChild | IProductDataBundleOptionChild[]) => {
                if (!child) {
                    return;
                }
                if (Array.isArray(child)) {
                    child.forEach(put);
                } else {
                    this._selection[child.selection_id] = child.qty;
                }
            };

            // local function to check if child is in stock
            const isSalable = (child?: IProductDataBundleOptionChild | IProductDataBundleOptionChild[]) => {
                if (!child) {
                    return;
                }
                if (Array.isArray(child)) {
                    return !find(child, i => !isSalable(i));
                }
                return child.product && child.product.isSalable(child.qty);
            };
            // take chldren by default selection (and normalized to array)
            const defaultSelection = compact(
                Array.isArray(this._bundleOption.defaultSelection)
                    ? this._bundleOption.defaultSelection
                    : [this._bundleOption.defaultSelection]
            ).map(i => this.getChild(<number>i));
            // if default selection is not empty then pass it to selection candidates
            if (defaultSelection.length && isSalable(defaultSelection)) {
                put(defaultSelection);
            } else if (!resetToDefaultMode) {
                const children = this.getChildrenSortedForSelection();
                // if default selection does not exist or is OO lets try best alternaticve
                const alternativeSelection = <IProductDataBundleOptionChild | undefined>find(children, isSalable);
                // if there is in stock alternative add it
                if (alternativeSelection) {
                    put(alternativeSelection);
                } else if (defaultSelection.length) {
                    // if no in stock alternative but defaults exist, then add it
                    put(defaultSelection);
                } else {
                    // otherwise just pick first child
                    put(first(children));
                }
            }
        }
    }

    protected getChildrenSortedForSelection() {
        return this._bundleOption.children;
    }

    /**
     * for radio/select options make sure we have no more then one selection
     */
    protected removeExtraSelections(selectionToStay?: number | string) {
        if (this._isOneSelection) {
            const selectionIds = keys(this._selection);

            // if more then one option selected then drop extras (leave one prefered)
            if (selectionIds.length > 1) {
                const selectionsToRemove =
                    // if prefered selection is given then prefer it to stay
                    selectionToStay
                        ? without(selectionIds, '' + selectionToStay)
                        : // otherwise leave 1st selection and remove others
                          drop(selectionIds, 1);

                each(selectionsToRemove, selectionId => delete this._selection[selectionId]);
            }
        }
    }

    /**
     * Removes bad selections
     */
    protected cleanupBadSelections() {
        for (const selectionId in this._selection) {
            if (this._selection[selectionId] <= 0) {
                delete this._selection[selectionId];
            }
        }
    }

    /**
     * Normalize selection to number
     * @param {number | IBundleSelectionOption} selection
     * @returns {number} selectionId
     */
    protected _toSelectionId(selection: number | IProductDataBundleOptionChild): number {
        return typeof selection === 'number' ? selection : selection.selection_id;
    }
}
